import React, { useCallback, useEffect, useState } from "react";
import { KeyRound, Loader2, Trash2, Plus } from "lucide-react";
import { startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";

// A passkey is the device itself: Face ID, a fingerprint or the device PIN. The password stays
// as the fallback, because a passkey lives on one device and devices get lost.
export const PasskeysCard = () => {
  const { t } = useLang();
  const { toast } = useToast();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const supported = browserSupportsWebAuthn();

  const load = useCallback(() => {
    api.get("/passkeys").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  const add = async () => {
    setBusy("add");
    try {
      const { data: start } = await api.post("/passkeys/register/options");
      const credential = await startRegistration({ optionsJSON: start.options });
      const label = /iphone|ipad|android/i.test(navigator.userAgent) ? "Phone" : "This computer";
      const { data } = await api.post("/passkeys/register/finish", { state: start.state, credential, label });
      setRows(data.passkeys);
      toast({ title: t("pk.added"), description: t("pk.addedNote") });
    } catch (e) {
      const cancelled = e?.name === "NotAllowedError" || e?.name === "AbortError";
      toast({ title: cancelled ? t("pk.cancelled") : t("pk.failed"), description: cancelled ? undefined : e.response?.data?.detail });
    } finally { setBusy(""); }
  };

  const remove = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.delete(`/passkeys/${id}`);
      setRows(data.passkeys);
      toast({ title: t("pk.removed") });
    } catch { toast({ title: t("pk.failed") }); }
    finally { setBusy(""); }
  };

  if (!rows) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="passkeys-card">
      <h3 className="font-bold text-foreground flex items-center gap-2"><KeyRound size={17} className="text-[#16a34a]" /> {t("pk.title")}</h3>
      <p className="text-xs text-muted-foreground mt-0.5">{t("pk.sub")}</p>

      {!supported ? (
        <p className="text-sm text-muted-foreground mt-3" data-testid="passkeys-unsupported">{t("pk.unsupported")}</p>
      ) : (
        <>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-3" data-testid="passkeys-empty">{t("pk.none")}</p>
          ) : (
            <ul className="mt-3 space-y-2" data-testid="passkeys-list">
              {rows.map((k) => (
                <li key={k.id} className="flex items-center justify-between gap-3 border border-border rounded-xl px-3 py-2.5" data-testid={`passkey-row-${k.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-foreground truncate">{k.label}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {k.lastUsedAt ? t("pk.lastUsed", { date: new Date(k.lastUsedAt).toLocaleDateString() }) : t("pk.never")}
                    </p>
                  </div>
                  <button onClick={() => remove(k.id)} disabled={busy === k.id} data-testid={`passkey-remove-${k.id}`}
                    className="text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg px-2.5 py-2 transition-colors flex items-center gap-1.5">
                    {busy === k.id ? <Loader2 className="animate-spin" size={13} /> : <Trash2 size={13} />} {t("pk.remove")}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button onClick={add} disabled={busy === "add"} data-testid="passkey-add"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white rounded-xl px-4 py-2.5 transition-colors">
            {busy === "add" ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />} {t("pk.add")}
          </button>
        </>
      )}
    </div>
  );
};

export default PasskeysCard;
